import { useRef, useState, useCallback, useLayoutEffect } from "react";

// Draggable before/after image comparison slider.
export function BeforeAfterSlider({ beforeSrc, afterSrc, beforeLabel = "Move-in", afterLabel = "Move-out" }) {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50);
  const [width, setWidth] = useState(0);
  const dragging = useRef(false);

  useLayoutEffect(() => {
    const measure = () => {
      if (containerRef.current) setWidth(containerRef.current.getBoundingClientRect().width);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const update = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, pct)));
  }, []);

  const onDown = (e) => { dragging.current = true; update(e.touches ? e.touches[0].clientX : e.clientX); };
  const onMove = (e) => { if (dragging.current) update(e.touches ? e.touches[0].clientX : e.clientX); };
  const onUp = () => { dragging.current = false; };

  return (
    <div
      ref={containerRef}
      data-testid="before-after-slider"
      className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden border border-[#E2E8F0] bg-black select-none touch-none cursor-ew-resize"
      onMouseDown={onDown}
      onMouseMove={onMove}
      onMouseUp={onUp}
      onMouseLeave={onUp}
      onTouchStart={onDown}
      onTouchMove={onMove}
      onTouchEnd={onUp}
    >
      {/* After (full, underneath) */}
      <img src={afterSrc} alt={afterLabel} className="absolute inset-0 w-full h-full object-cover pointer-events-none" draggable={false} />
      <span className="absolute top-3 right-3 z-10 rounded-full bg-black/60 text-white text-xs font-semibold px-2.5 py-1 pointer-events-none">{afterLabel}</span>

      {/* Before (clipped from left) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ width: `${pos}%` }}>
        <img
          src={beforeSrc}
          alt={beforeLabel}
          className="absolute inset-0 h-full object-cover max-w-none"
          style={{ width: width || "100%" }}
          draggable={false}
        />
        <span className="absolute top-3 left-3 rounded-full bg-brand text-white text-xs font-semibold px-2.5 py-1">{beforeLabel}</span>
      </div>

      {/* Divider + handle */}
      <div className="absolute top-0 bottom-0 z-20 pointer-events-none" style={{ left: `calc(${pos}% - 1px)` }}>
        <div className="w-0.5 h-full bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.15)]" />
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 left-1/2 h-11 w-11 rounded-full bg-white shadow-lg grid place-items-center">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0047FF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="13 17 8 12 13 7" /><polyline points="18 17 13 12 18 7" />
          </svg>
        </div>
      </div>
    </div>
  );
}
